import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDate } from '../../lib/format';
import { CustomerCreateDrawer } from './CustomerCreateDrawer';
import { exportCustomersCsv, listCustomers, type Customer } from './api';
import { stageLabel, stageTone } from './stage';

const { Text } = Typography;

export function CustomerListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['customers', search], (page) =>
    listCustomers({ ...page, q: search }),
  );

  const download = async (): Promise<void> => {
    try {
      const csv = await exportCustomersCsv(search);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'customers.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error('Could not build the export. Try again.');
    }
  };

  return (
    <>
      <ListPage<Customer>
        title={t('customers.title')}
        searchPlaceholder={t('customers.search')}
        onSearch={(value) => {
          setSearch(value);
        }}
        loading={isFetching}
        emptyDescription={search.length > 0 ? t('customers.noResults') : t('customers.empty')}
        actions={
          <>
            <Button icon={<DownloadOutlined />} onClick={() => void download()}>
              {t('customers.exportCsv')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('customers.new')}
            </Button>
          </>
        }
        columns={[
          { title: t('fields.customerNo'), dataIndex: 'customerNo', width: 190 },
          { title: t('fields.name'), dataIndex: 'name' },
          { title: t('fields.phone'), dataIndex: 'phone', width: 160 },
          { title: t('fields.email'), dataIndex: 'email', render: (v: string | null) => v ?? '—' },
          {
            title: t('fields.stage'),
            dataIndex: 'lifecycleStage',
            width: 120,
            render: (v: string) => <Tag color={stageTone(v)}>{stageLabel(v)}</Tag>,
          },
          {
            title: 'Added',
            dataIndex: 'createdAt',
            width: 140,
            render: (v: string) => formatDate(v),
          },
        ]}
        rows={rows}
        hasMore={hasMore}
        onLoadMore={loadMore}
        rowKey={(row) => row.uid}
        onRowClick={(row) => {
          void navigate(`/customers/${row.uid}`);
        }}
        renderMobileRow={(row) => (
          <Flex vertical gap={2}>
            <Flex justify="space-between" align="center" gap={8}>
              <Text strong>{row.name}</Text>
              <Tag color={stageTone(row.lifecycleStage)} style={{ marginInlineEnd: 0 }}>
                {stageLabel(row.lifecycleStage)}
              </Tag>
            </Flex>
            <Text type="secondary">{row.phone}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.customerNo}
            </Text>
          </Flex>
        )}
      />

      <CustomerCreateDrawer
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(uid) => {
          setCreating(false);
          void queryClient.invalidateQueries({ queryKey: ['customers'] });
          void message.success('Customer created.');
          void navigate(`/customers/${uid}`);
        }}
        onOpenMatch={(uid) => {
          setCreating(false);
          void navigate(`/customers/${uid}`);
        }}
      />
    </>
  );
}
